import React from 'react'
import { Col, List, Modal, Radio, Row, Typography } from 'antd'
import { Formik, FormikProps } from 'formik'
import { useTranslation } from 'react-i18next'

import { Input } from 'src/components/Formik'
import {
  PolicyFlowConference,
  PolicyFlowFormat,
  PolicyFlowType
} from 'src/types'

import * as CSS from './styles'
import * as Values from './initial-values'

interface Props {
  disabled?: boolean
  formikProps: FormikProps<PolicyFlowConference>
  isLoading?: boolean
  isVisible: boolean
  modalPolicyFlowFormat: PolicyFlowFormat
  modalPolicyFlowType: PolicyFlowType
  refFormik: React.MutableRefObject<Formik<PolicyFlowConference>>
  setModalPolicyFlowFormat?: React.Dispatch<
    React.SetStateAction<PolicyFlowFormat>
  >
  setModalPolicyFlowType?: React.Dispatch<React.SetStateAction<PolicyFlowType>>
}

export default function FlowConferencesModal({
  disabled = false,
  formikProps,
  isLoading = false,
  isVisible,
  modalPolicyFlowFormat,
  modalPolicyFlowType,
  refFormik,
  setModalPolicyFlowFormat,
  setModalPolicyFlowType
}: Props) {
  const { t } = useTranslation()

  const policyFlowType = [
    {
      description: t('web.policies-flow.conference.type.hierarchy-description'),
      title: t('web.policies-flow.type.hierarchy'),
      value: PolicyFlowType.HIERARCHY
    },
    {
      description: t(
        'web.policies-flow.conference.type.cost-center-description'
      ),
      title: t('web.policies-flow.type.cost-center'),
      value: PolicyFlowType.COST_CENTER
    },
    {
      description: t('web.policies-flow.conference.type.reason-description'),
      title: t('web.policies-flow.type.reason'),
      value: PolicyFlowType.REASON
    }
  ]

  const policyFlowFormat = [
    {
      title: t('web.policies-flow.format.standard'),
      value: PolicyFlowFormat.STANDARD,
      description: t('web.policies-flow.conference.format.standard-description')
    },
    {
      description: t(
        'web.policies-flow.conference.format.automatic-description'
      ),
      title: t('web.policies-flow.format.automatic'),
      value: PolicyFlowFormat.AUTOMATIC
    },
    {
      description: t(
        'web.policies-flow.conference.format.consensus-description'
      ),
      title: t('web.policies-flow.format.consensus'),
      value: PolicyFlowFormat.CONSENSUS
    }
  ]

  const title = t('web.policies-flow.conference.modal-title')

  return (
    <Modal
      cancelButtonProps={{ loading: isLoading }}
      centered
      closable
      destroyOnClose
      okButtonProps={{
        hidden: disabled,
        loading: isLoading
      }}
      onCancel={() => {
        refFormik.current?.resetForm(Values.initialValues)
      }}
      onOk={(e: any) => {
        refFormik.current?.handleSubmit(e)
      }}
      title={title}
      visible={isVisible}
      width={800}
    >
      <Row gutter={24}>
        <Col span={6}>
          <Input
            data-test-id="modal-conference-code"
            disabled={disabled}
            formikProps={formikProps}
            label="web.tree.node.code"
            name="code"
            placeholder="web.tree.node.code-placeholder"
          />
        </Col>
        <Col span={18}>
          <Input
            data-test-id="modal-conference-name"
            disabled={disabled}
            formikProps={formikProps}
            label="web.tree.node.name"
            name="name"
            placeholder="web.tree.node.name-placeholder"
          />
        </Col>
      </Row>
      <Row gutter={24}>
        <Col span={24}>
          <Input
            data-test-id="modal-conference-description"
            disabled={disabled}
            formikProps={formikProps}
            label="web.resources.common.description.title"
            name="description"
            placeholder="web.resources.common.description.placeholder"
            required={false}
          />
        </Col>
      </Row>
      <Row gutter={[24, 0]}>
        <Col span={24}>
          <CSS.ModalTitle>
            {t('web.policies-flow.conference.type.title')}
          </CSS.ModalTitle>
        </Col>
      </Row>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Radio.Group
            defaultValue={PolicyFlowType.HIERARCHY}
            disabled={disabled}
            style={{ width: '100%' }}
            value={modalPolicyFlowType}
          >
            <List
              data-test-id="modal-conference-type"
              dataSource={policyFlowType}
              itemLayout="horizontal"
              renderItem={item => (
                <Row
                  gutter={[2, 2]}
                  onClick={() => {
                    if (!disabled) {
                      setModalPolicyFlowType(item.value)
                      let cost_center_conference = false
                      let reason_conference = false
                      switch (item.value) {
                        case PolicyFlowType.COST_CENTER:
                          cost_center_conference = true
                          reason_conference = false
                          break
                        case PolicyFlowType.REASON:
                          cost_center_conference = false
                          reason_conference = true
                      }

                      formikProps.setFieldValue(
                        'cost_center_conference',
                        cost_center_conference
                      )
                      formikProps.setFieldValue(
                        'reason_conference',
                        reason_conference
                      )
                    }
                  }}
                >
                  <Col span={5}>
                    <Typography.Text strong>{item.title}</Typography.Text>
                  </Col>
                  <Col span={1}>
                    <Radio value={item.value} />
                  </Col>
                  <Col span={18}>
                    <CSS.ModalDescription>
                      {item.description}
                    </CSS.ModalDescription>
                  </Col>
                </Row>
              )}
            />
          </Radio.Group>
        </Col>
      </Row>
      <Row gutter={24}>
        <Col span={24}>
          <CSS.ModalTitle>
            {t('web.policies-flow.conference.format.title')}
          </CSS.ModalTitle>
        </Col>
      </Row>
      <Row align="middle" gutter={[24, 24]} justify="space-between">
        <Col span={24}>
          <Radio.Group
            defaultValue={PolicyFlowFormat.STANDARD}
            disabled={disabled}
            style={{ width: '100%' }}
            value={modalPolicyFlowFormat}
          >
            <List
              data-test-id="modal-conference-format"
              dataSource={policyFlowFormat}
              itemLayout="horizontal"
              renderItem={item => (
                <Row
                  gutter={[2, 2]}
                  onClick={() => {
                    if (!disabled) {
                      setModalPolicyFlowFormat(item.value)
                      let automatic_conference = false
                      let consensual_conference = false
                      switch (item.value) {
                        case PolicyFlowFormat.AUTOMATIC:
                          automatic_conference = true
                          consensual_conference = false
                          break
                        case PolicyFlowFormat.CONSENSUS:
                          automatic_conference = false
                          consensual_conference = true
                      }

                      formikProps.setFieldValue(
                        'automatic_conference',
                        automatic_conference
                      )
                      formikProps.setFieldValue(
                        'consensual_conference',
                        consensual_conference
                      )
                    }
                  }}
                >
                  <Col span={5}>
                    <Typography.Text strong>{item.title}</Typography.Text>
                  </Col>
                  <Col span={1}>
                    <Radio value={item.value} />
                  </Col>
                  <Col span={18}>
                    <CSS.ModalDescription>
                      {item.description}
                    </CSS.ModalDescription>
                  </Col>
                </Row>
              )}
            />
          </Radio.Group>
        </Col>
      </Row>
      <Row gutter={[12, 12]}>
        <Col span={24}>
          <CSS.ModalTitle>
            {t('web.policies-flow.conference.exception.title')}
          </CSS.ModalTitle>
        </Col>
      </Row>
      <Row align="middle" gutter={24} justify="space-between">
        <Col span={24}>
          <div
            onClick={() => {
              if (!disabled) {
                formikProps.setFieldValue(
                  'exception_checker',
                  !formikProps.values.exception_checker
                )
              }
            }}
          >
            <CSS.Switch
              checked={formikProps.values.exception_checker}
              data-test-id="modal-conference-exception"
              disabled={disabled}
            />
            <CSS.ModalDescription>
              {t('web.policies-flow.conference.exception.description')}
            </CSS.ModalDescription>
          </div>
        </Col>
      </Row>
    </Modal>
  )
}
